import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSwitch } from '../../reducers/switch';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import FilterIcon from '../../assets/icons/filter-icon.svg?react';
import FilterClearIcon from '../../assets/icons/filter-clear-icon.svg?react';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';
import moment from 'moment';
import NoItemsFound from '../../components/NoItemsFound';
import Pagination from '../../components/CustomPagination';
import SearchInput from '../../components/SearchInput';
import { useNavigate } from 'react-router';
import { useDebounce } from '@uidotdev/usehooks';
import Loader from '../../components/Loader';
import {
    GetRequestAccessResponse,
    Organization,
    OnboardOrganizationControllerGetRequestAccessStatusEnum,
    ManualSubscriptionStatusEnum,
    User,
} from '@EHSNavigator/backend';

type OrganizationRequest = User;
import { useApiClients } from '../../client/api/useApiClient';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { toggleModal } from '../../reducers/modalToggle';
import ModalComponent from '../../components/ModalComponent';
import ApproveOrganizationModal from '../../components/ApproveOrganizationModal';
import RejectOrganizationModal from '../../components/RejectOrganizationModal';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Select from '../../components/Select';
import OrganizationRequestActions from '../../components/OrganizationRequestActions';

interface OrganizationRequestAccessProps {
    otherClasses?: string;
    data?: GetRequestAccessResponse;
    noDataMessage: string;
    params: {
        search: string;
        page: number;
        limit: number;
        status?: OnboardOrganizationControllerGetRequestAccessStatusEnum;
    };
    isOrganizationsLoading: boolean;
    refetch: () => void;
}


const rejectSchema = Yup.object().shape({
    description: Yup.string(),
});

const approveSchema = Yup.object().shape({
    amount: Yup.number()
        .typeError('Amount must be a number')
        .min(0, 'Amount cannot be negative')
        .nullable()
        .notRequired(),
});

const OrganizationRequestAccess: React.FC<OrganizationRequestAccessProps> = ({
    otherClasses,
    data,
    noDataMessage,
    params,
    isOrganizationsLoading,
    // refetch,
}) => {
    const dashboardClasses = clsx(otherClasses, 'flex flex-col w-full flex-1');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { OnboardOrganizationApi } = useApiClients();
    const queryClient = useQueryClient();

    const [state, setState] = useState<{
        page: number;
        search: string;
        limit: number;
        status: OnboardOrganizationControllerGetRequestAccessStatusEnum | '';
    }>({
        page: params?.page || 1,
        search: params?.search || '',
        limit: params?.limit || 10,
        status: params?.status || '',
    });

    const [selectedOrganization, setSelectedOrganization] = useState<OrganizationRequest | null>(null);

    const debouncedSearch = useDebounce(state?.search, 600);
    const prevFiltersRef = useRef({});
    const isFirstRun = useRef(true);

    const modalName = useSelector(
        (state: { modalToggle: { modalName: string } }) =>
            state.modalToggle.modalName,
    );

    useEffect(() => {
        const filters = {
            search: debouncedSearch,
            limit: state.limit,
            status: state.status,
        };

        let shouldResetPage = false;
        if (!isFirstRun.current) {
            const filtersChanged =
                JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current);
            if (filtersChanged) {
                shouldResetPage = true;
                setState((prev) => ({ ...prev, page: 1 }));
            }
        }

        const paramsSearch = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
            if (key === 'limit' && Number(value) === 10) {
                return;
            }
            if (value && value !== '') {
                paramsSearch.set(key, value.toString());
            }
        });
        if (state.page !== 1 && !shouldResetPage) {
            paramsSearch.set('page', state.page.toString());
        }
        if (shouldResetPage) {
            paramsSearch.delete('page');
            setState((prev) => ({ ...prev, page: 1 }));
        }
        prevFiltersRef.current = filters;
        if (!isFirstRun.current) {
            navigate(`/admin/organization-requests?${paramsSearch.toString()}`);
        }
        isFirstRun.current = false;
    }, [debouncedSearch, state.limit, state.page, state.status, navigate]);

    const updateQuery = (queryName: string, queryValue: string) => {
        const params = new URLSearchParams(window.location.search);
        if (queryName === 'page' && queryValue === '1') {
            params.delete('page');
        } else if (queryValue === '') {
            params.delete(queryName);
        } else {
            params.set(queryName, queryValue);
        }
        navigate(`/admin/organization-requests?${params.toString()}`);
    };

    const handlePageChange = (newPage: number) => {
        setState((prev) => ({ ...prev, page: newPage }));
        updateQuery('page', String(newPage));
    };

    const rejectHandler = useFormik({
        initialValues: { description: '' },
        validationSchema: rejectSchema,
        onSubmit: async (values) => {
            if (!selectedOrganization) return;
            try {
                const response = await OnboardOrganizationApi.onboardOrganizationControllerReject({
                    rejectDto: {
                        uid: selectedOrganization.id || '',
                        requestType: selectedOrganization.org.manualSubscription?.status === ManualSubscriptionStatusEnum.RequestExtension ?
                            ManualSubscriptionStatusEnum.RejectedExtension : ManualSubscriptionStatusEnum.Rejected,
                        rejectMessage: values.description || undefined,
                    },
                });
                toast.success(response?.data?.message || 'Organization rejected successfully');
                rejectHandler.resetForm();
                dispatch(toggleModal(''));
                setSelectedOrganization(null);
                queryClient.setQueryData(
                    ['get-organization-request-access',
                        params?.search,
                        params?.page,
                        params?.limit,
                        params?.status,

                    ],
                    (oldData: any) => {
                        const updatedOrgWithCurrentStatus = {
                            ...oldData,
                            organizations: oldData?.organizations?.map(
                                (node: any) =>
                                    node.id === response.data?.user?.id ? { ...response.data?.user } : node,
                            ),
                        };
                        return updatedOrgWithCurrentStatus;
                    },
                );
            } catch (error: any) {
                const errorMessage = error?.response?.data?.message;
                if (Array.isArray(errorMessage)) {
                    errorMessage.forEach((msg: string) => toast.error(msg));
                } else {
                    toast.error(errorMessage || 'An unexpected error occurred.');
                }
            }
        },
    });

    const approveHandler = useFormik({
        initialValues: { amount: '' },
        validationSchema: approveSchema,
        onSubmit: async (values) => {
            console.log(values, 'values');
            if (!selectedOrganization) return;

            try {
                const response = await OnboardOrganizationApi.onboardOrganizationControllerApprove({
                    approveDto: {
                        uid: selectedOrganization.id || '',
                        amount: Number(values.amount) || undefined,
                    },
                });
                toast.success(response?.data?.message || 'Organization approved successfully');
                approveHandler.resetForm();
                dispatch(toggleModal(''));
                setSelectedOrganization(null);

                queryClient.setQueryData(['get-organization-request-access',
                    params?.search,
                    params?.page,
                    params?.limit,
                    params?.status,
                ],
                    (oldData: any) => {
                        const updatedInvestigationQuestions = {
                            ...oldData,
                            organizations: oldData?.organizations?.filter(
                                (node: any) => node.id !== response.data?.user?.id,
                            ),
                        };
                        return updatedInvestigationQuestions;
                    },
                );
            } catch (error: any) {
                const errorMessage = error?.response?.data?.message;
                if (Array.isArray(errorMessage)) {
                    errorMessage.forEach((msg: string) => toast.error(msg));
                } else {
                    toast.error(errorMessage || 'An unexpected error occurred.');
                }
            }
        },
    });

    const handleApprove = (org: Organization) => {
        const userWithOrg = organizations.find((u) => u.org?.id === org.id);
        if (userWithOrg) {
            setSelectedOrganization(userWithOrg);
            dispatch(toggleModal('approve-organization'));
        }
    };

    const handleReject = (org: Organization) => {
        const userWithOrg = organizations.find((u) => u.org?.id === org.id);
        if (userWithOrg) {
            setSelectedOrganization(userWithOrg);
            dispatch(toggleModal('reject-organization'));
        }
    };

    const organizations: OrganizationRequest[] = (data?.organizations || []) as OrganizationRequest[];
    const meta = data?.meta;

    const getStatusColor = (status?: ManualSubscriptionStatusEnum | string) => {
        switch (status) {
            case ManualSubscriptionStatusEnum.Active:
                return 'text-brand-papery';
            case ManualSubscriptionStatusEnum.RequestAccess:
                return 'text-brand-fair-orange';
            case ManualSubscriptionStatusEnum.RequestExtension:
                return 'text-brand-byzantine';
            case ManualSubscriptionStatusEnum.Pending:
                return 'text-brand-fair-orange';
            case ManualSubscriptionStatusEnum.Rejected:
            case ManualSubscriptionStatusEnum.RejectedExtension:
            case ManualSubscriptionStatusEnum.Cancelled:
                return 'text-brand-posh-pink';
            case ManualSubscriptionStatusEnum.Expired:
                return 'text-neutral-tones-text/50';
            default:
                return 'text-neutral-tones-text/50';
        }
    };

    return (
        <div className={dashboardClasses} data-testid="organization-request-access">
            <div className="flex items-start gap-2.5">
                <button
                    className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
                    onClick={() => dispatch(toggleSwitch())}
                >
                    <ToggleIcon />
                </button>
                <div>
                    <h1 className="h3">Organization Request Access</h1>
                    <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
                        Manage access requests from manually onboarded organizations
                    </p>
                </div>
            </div>
            <p className="font-inter-variable text-neutral-tones-text/80 -tracking-text-area mt-15 text-lg leading-5 font-medium lg:hidden">
                Manage access requests from manually onboarded organizations
            </p>
            <hr className="border-neutral-tones-text/50 mt-9 mb-6 lg:hidden" />
            <SearchInput
                name="search"
                onChange={(e) =>
                    setState((prev) => ({
                        ...prev,
                        search: e?.target?.value,
                    }))
                }
                placeholder="Enter organization name or email..."
                value={state?.search}
                disabled={false}
                errorMessage=""
                otherClasses={{ container: 'w-full lg:mt-12' }}
            />

            <div className="border-b-neutral-tones-text/50 relative flex flex-col gap-4 border-b py-6 lg:flex-row lg:items-center lg:gap-9">
                <p className="font-inter-variable -tracking-form-input lg:-tracking-text-area text-brand-byzantine flex items-center gap-2.5 object-fill text-base leading-5 font-bold lg:text-lg">
                    <button
                        onClick={() => {
                            if (
                                state?.search !== '' ||
                                state?.status !== '' ||
                                state?.limit !== 10
                            ) {
                                setState((prev) => ({
                                    ...prev,
                                    search: '',
                                    status: '',
                                    limit: 10,
                                }));
                            }
                        }}
                        className="font-inter-variable -tracking-form-input lg:-tracking-text-area text-brand-byzantine flex cursor-pointer items-center gap-2.5 object-fill text-base leading-5 font-bold lg:text-lg"
                    >
                        {state?.search !== '' ||
                            state?.status !== '' ||
                            state?.limit !== 10 ? (
                            <FilterClearIcon className="[&>path]:fill-brand-byzantine h-6 w-6 lg:h-9 lg:w-9" />
                        ) : (
                            <FilterIcon className="h-6 w-6 lg:h-9 lg:w-9" />
                        )}
                        Filters
                    </button>
                </p>
                <div className="flex flex-1 items-center gap-6">
                    <Select
                        filterSelect
                        list={Object.values(OnboardOrganizationControllerGetRequestAccessStatusEnum).map((status) => ({
                            label: status,
                            value: status,
                        }))}
                        value={state?.status}
                        placeholder="Status"
                        otherClasses={{
                            container: 'w-full max-w-44',
                        }}
                        onSelect={(e) =>
                            setState((prev) => ({
                                ...prev,
                                status: e as OnboardOrganizationControllerGetRequestAccessStatusEnum | '',
                            }))
                        }
                        errorMessage=""
                    />
                </div>
            </div>

            <div className="global-scrollbar-style mt-12 min-h-52 flex-1 overflow-auto px-0.5">
                {isOrganizationsLoading ? (
                    <Loader
                        variant="compact"
                        otherClasses="bg-neutral-tones-background/50 z-20 backdrop-blur-[2px] lg:!w-[calc(100%-310px)] lg:right-0 lg:left-auto !fixed !top-0 left-0 h-dvh"
                    />
                ) : organizations.length > 0 ? (
                    <div className="w-full min-w-6xl">
                        <div className="border-neutral-tones-text/50 grid grid-cols-12 gap-4 border-b pb-6">
                            <h6 className="pe-8 text-end text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                                Sr.
                            </h6>
                            <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                                Organization Name
                            </h6>
                            <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                                Owner Name
                            </h6>
                            <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                                License
                            </h6>
                            <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                                Status
                            </h6>
                            <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                                Date Created
                            </h6>
                            <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                                Actions
                            </h6>
                        </div>
                        <div>
                            {organizations.map((org: OrganizationRequest, index: number) => (
                                <div
                                    key={org.id}
                                    className="border-b-neutral-tones-text/50 grid grid-cols-12 gap-4 border-b py-5"
                                >
                                    <p className="pe-8 text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-1 flex items-center justify-between text-sm leading-4 font-bold lg:text-base lg:leading-5 lg:font-normal">
                                        <span>
                                            {
                                                ((org.org?.manualSubscription?.status === ManualSubscriptionStatusEnum.Rejected ||
                                                    org.org?.manualSubscription?.status === ManualSubscriptionStatusEnum.RejectedExtension) &&
                                                    org.org?.manualSubscription?.rejectedMessage) && (
                                                    <button className="group relative">
                                                        <WarningIcon className="h-5 min-h-5 w-5 min-w-5 [&>g>path]:fill-brand-posh-pink" />
                                                        <span className="bg-neutral-tones-sidebar text-neutral-tones-text absolute bottom-6 left-0 z-10 hidden min-w-48 max-w-48  items-center justify-center rounded px-2 py-1 text-xs font-normal shadow-lg group-hover:block group-focus:block">
                                                            {org.org.manualSubscription.rejectedMessage}
                                                        </span>
                                                    </button>
                                                )}
                                        </span>
                                        <span className='font-bold'>
                                            {meta?.page && meta?.limit
                                                ? (meta.page - 1) * meta.limit + index + 1
                                                : index + 1}
                                        </span>
                                    </p>
                                    <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 flex items-center text-sm leading-4 font-normal capitalize lg:text-base lg:leading-5 break-words">
                                        {org.org?.name}
                                    </p>
                                    <p className="min-w-0 text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 flex items-center text-sm leading-4 font-normal lg:text-base lg:leading-5 capitalize">
                                        <span className="truncate block w-full">
                                            {org?.fullName}
                                        </span>
                                    </p>
                                    <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-1 flex items-center text-sm leading-4 font-normal lg:text-base lg:leading-5">
                                        {org.org?.manualSubscription?.licenseCount || 0}
                                    </p>
                                    <div className="col-span-2 flex items-center">
                                        <span
                                            className={clsx(
                                                'font-inter-variable -tracking-form-input text-sm leading-4 font-bold uppercase lg:text-base lg:leading-5',
                                                getStatusColor(org.org?.manualSubscription?.status),
                                            )}
                                        >
                                            {org.org?.manualSubscription?.status.replace('_', ' ')}
                                        </span>
                                    </div>
                                    <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 flex items-center text-sm leading-4 font-normal lg:text-base lg:leading-5">
                                        {org.createdAt
                                            ? moment(org.createdAt).format('MMMM D, YYYY')
                                            : 'N/A'}
                                    </p>
                                    <div className="col-span-2 flex items-center gap-2">
                                        {org.org && (
                                            <OrganizationRequestActions
                                                organization={org.org}
                                                status={org.org.manualSubscription?.status}
                                                onApprove={handleApprove}
                                                onReject={handleReject}
                                                isApproveLoading={approveHandler.isSubmitting}
                                                isRejectLoading={rejectHandler.isSubmitting}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <NoItemsFound message={noDataMessage} />
                )}
            </div>
            {organizations.length > 0 && meta && (
                <Pagination
                    currentPage={state.page}
                    totalPages={meta.totalPages}
                    totalItems={meta.total}
                    limit={meta.limit}
                    showingItems={organizations.length}
                    onPageChange={handlePageChange}
                    onLimitChange={(e) =>
                        setState((prev) => ({ ...prev, limit: e, page: 1 }))
                    }
                    otherClasses=""
                />
            )}

            {
                ['approve-organization', 'reject-organization'].includes(modalName) && (
                    <ModalComponent
                        otherClasses="px-4 sm:px-0 py-4"
                        additionalClass={clsx('w-full max-w-573 rounded-2xl p-5 lg:px-9 lg:py-6  h-fit max-h-full')}
                        customClose={() => {
                            dispatch(toggleModal(''));
                            setSelectedOrganization(null);
                        }}
                    >
                        {
                            modalName === 'approve-organization' && (
                                <ApproveOrganizationModal
                                    organization={selectedOrganization?.org}
                                    formHandler={approveHandler}
                                    onClose={() => {
                                        dispatch(toggleModal(''));
                                        approveHandler.resetForm();
                                        setSelectedOrganization(null);
                                    }}
                                />
                            )
                        }

                        {
                            modalName === 'reject-organization' && (
                                <RejectOrganizationModal
                                    organization={selectedOrganization?.org}
                                    formHandler={rejectHandler}
                                    onClose={() => {
                                        dispatch(toggleModal(''));
                                        rejectHandler.resetForm();
                                        setSelectedOrganization(null);
                                    }}
                                />
                            )
                        }
                    </ModalComponent>
                )
            }
        </div>
    );
};

export default OrganizationRequestAccess;

